import { MediaOrderedDetails } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { FixaQuantityStepper } from "@coworker/apprestructured/src/shared/wrappers/FixaQuantityStepper/FixaQuantityStepper";
import React from "react";
import { Loading } from "@coworker/apprestructured/src/task-management/loading";
import { MediasAndArticleCountResponse } from "@coworker/app/src/components/RoomSettings/types/media";
import { useTranslation } from "@coworker/locales";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import { MediaTypes } from "@coworker/app/src/components/RoomSettings/constants";

interface MediasQuantityContentProps {
  mediasOrderedDetails: MediaOrderedDetails[] | null | Loading;
  mediasData: MediasAndArticleCountResponse[];
  handlePiecesChange: (
    mediaId: string,
    articleId: string,
    newPieces: number
  ) => void;
  handlePTagsChange: (
    mediaId: string,
    articleId: string,
    newPTags: number
  ) => void;
}

export const MediasQuantityContent = ({
  mediasOrderedDetails,
  mediasData,
  handlePiecesChange,
}: MediasQuantityContentProps) => {
  const { t } = useTranslation();

  // Use composite key (mediaId:articleId) since same media can have multiple articles
  const detailsByCompositeKey = new Map(
    Array.isArray(mediasOrderedDetails)
      ? mediasOrderedDetails.map(
          ({ id, article_id, pieces, pieces_ordered, ptags_ordered }) => [
            `${id}:${article_id}`,
            { pieces, pieces_ordered, ptags_ordered },
          ]
        )
      : []
  );

  // Group medias by type or area for better organization
  const groupedMedias = mediasData.reduce(
    (acc, media) => {
      const groupKey = media.mediaType || "Other";
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(media);
      return acc;
    },
    {} as Record<string, MediasAndArticleCountResponse[]>
  );

  return (
    <>
      <FixaInlineMessage
        variant="informative"
        title={
          t("changeOrConfirmQuantityForEachMediaString") ||
          "Change or confirm the needed quantity for each media."
        }
        body={
          t("quantityCannotExceedExistingAmountString") ||
          "The quantity cannot exceed the existing amount in each media."
        }
      />

      {Object.entries(groupedMedias).map(([mediaType, medias]) => (
        <FixaListView key={mediaType} id={`media-type-${mediaType}`}>
          <FixaListViewItem
            size="small"
            description={mediaType}
            key={`${mediaType}-header`}
          />
          {medias.map((media) => {
            const compositeKey = `${media.mediaId}:${media.articleId}`;
            const details = detailsByCompositeKey.get(compositeKey);
            const pieces = details?.pieces ?? 0;
            const piecesOrdered = details?.pieces_ordered ?? pieces;

            return (
              <React.Fragment key={compositeKey}>
                <FixaListViewItem
                  showDivider={false}
                  size="medium"
                  title={
                    <FixaText textStyle="Heading.S">
                      {mediaType === MediaTypes.ROOM && media.parentMediaName
                        ? media.parentMediaName
                        : media.name}
                    </FixaText>
                  }
                  description={
                    <FixaText textStyle="Body.M">
                      {mediaType === MediaTypes.ROOM && media.parentMediaName
                        ? `${media.name}${media.groupName ? ` > ${media.groupName}` : ""}`
                        : media.groupName}
                    </FixaText>
                  }
                  quantityLabel={
                    <FixaText textStyle="Body.M">
                      {t("piecesCountString", { count: pieces })}
                    </FixaText>
                  }
                />
                <div style={{ padding: "8px 16px", marginBottom: "16px" }}>
                  <FixaQuantityStepper
                    small
                    fluid
                    defaultValue={piecesOrdered}
                    ariaDescribedById={`media-pieces-quantity-stepper-${compositeKey}`}
                    ariaDescribedBy={`pieces-stepper-${compositeKey}`}
                    minVal={0}
                    maxVal={pieces}
                    onQuantityChange={(newQuantity) =>
                      handlePiecesChange(
                        media.mediaId,
                        media.articleId,
                        Number(newQuantity)
                      )
                    }
                  />
                </div>
              </React.Fragment>
            );
          })}
        </FixaListView>
      ))}
    </>
  );
};
